import toast from "react-hot-toast";

import { useMutation } from "@tanstack/react-query";
import { PinIcon, Trash2Icon } from "lucide-react";

import { formatDateAt } from "simplydo/core/dates";
import { getImageUrl } from "simplydo/core/images";
import { hasPermission } from "simplydo/core/permissions";
import { asMutation } from "simplydo/core/query";
import { RichTextViewer } from "simplydo/react/richtext";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import { FeedItemOfType } from "../types";
import {
  FeedItem,
  FeedItemActions,
  FeedItemContent,
  FeedItemDate,
  FeedItemIcon,
  FeedItemMessage,
} from "./FeedItemUI";

type OrganisationActivityFeedPostFeedItemProps = {
  item: FeedItemOfType<"organisationActivityFeedPost">;
};

const OrganisationActivityFeedPostFeedItem = ({
  item,
}: OrganisationActivityFeedPostFeedItemProps) => {
  const user = useCurrentUser();
  const organisation = user?.ownerOrganisation;

  const canManage = hasPermission(
    user,
    "org.viewDashboard",
    user!.organisation,
  );

  const deleteMutation = useMutation({
    mutationFn: asMutation(api.organisations.deletePostFromActivityFeed)!,
    onSuccess: () => {
      toast.success("Post deleted");
      api.setInfiniteQueryData(api.users.feed, [], (prev) => ({
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          feed: page.feed.filter((p) => p._id !== item._id),
        })),
      }));
    },
    onError: (err) => toast.error(err.message),
  });

  const handleDelete = () => {
    util
      .confirm(
        "Delete activity feed post",
        "The post will be deleted and no longer shown in any activity feeds in this organisation. This action is irreversible",
      )
      .then(() => {
        deleteMutation.mutate([user!.organisation, item._id]);
      })
      .catch(() => {});
  };

  const pinMutation = useMutation({
    mutationFn: asMutation(api.organisations.updateActivityFeedPost)!,
    onSuccess: (data, vars) => {
      api.setInfiniteQueryData(api.users.feed, [], (prev) => ({
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          feed: page.feed.map((p) =>
            p._id === item._id ? { ...p, isPinned: vars[2].isPinned } : p,
          ),
        })),
      }));
      api.invalidateQueries(api.users.feed);
    },
    onError: (err) => toast.error(err.message),
  });

  const handlePin = () => {
    util
      .confirm(
        item.isPinned ? "Unpin post" : "Pin post",
        item.isPinned
          ? "The post will be unpinned and no longer shown at the top of the activity feed."
          : "The post will be pinned and shown at the top of the activity feed.",
      )
      .then(() => {
        pinMutation.mutate([
          user!.organisation,
          item._id,
          { isPinned: !item.isPinned },
        ]);
      })
      .catch(() => {});
  };

  return (
    <FeedItem>
      <FeedItemIcon
        pinned={item.isPinned}
        src={getImageUrl(
          organisation,
          "darkLogo",
          ["200x200"],
          "images/defaults/profile-logo.png",
        )}
      />
      <FeedItemContent>
        <FeedItemMessage>
          {item.owner ? (
            <>
              <b>
                <UserChip user={item.owner} compact="very" />
              </b>{" "}
            </>
          ) : null}
          <span>
            {item.owner ? (
              <>
                posted a message
                {organisation?.name ? (
                  <>
                    {" "}
                    for <b style={{ color: "#000" }}>{organisation.name}</b>
                  </>
                ) : null}
                .
              </>
            ) : (
              <>
                <b style={{ color: "#000" }}>
                  {organisation?.name || "Your organisation"}
                </b>{" "}
                posted a message.
              </>
            )}
          </span>
        </FeedItemMessage>
        <RichTextViewer restrictInitialContentHeight value={item.message} />
        <FeedItemDate>{formatDateAt(item.createdAt)}</FeedItemDate>
        {canManage ? (
          <FeedItemActions>
            <Button
              size="small"
              color={canManage && item.isPinned ? "green" : "grey"}
              variant="ghost"
              onClick={handlePin}
            >
              <Icon icon={PinIcon} />
            </Button>
            <Button
              variant="ghost"
              color="grey"
              size="small"
              onClick={handleDelete}
            >
              <Icon icon={Trash2Icon} />
            </Button>
          </FeedItemActions>
        ) : null}
      </FeedItemContent>
    </FeedItem>
  );
};

export default OrganisationActivityFeedPostFeedItem;
